import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import MenuService from '@/services/CmsServices/MenuService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    errors: null,
    successOnMenuUpdate: '',
    successOnLabelUpdate: '',
    parentMenuList: [],
    sortMenuList: [],
    advancedMenuList: [],
    loading: false,
};

export const viewMenu = createAsyncThunk(
    'menu/viewMenu',
    async (menuId, { rejectWithValue }) => {
        try {
            const response = await MenuService.viewMenu(menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const updateSimpleMenu = createAsyncThunk(
    'menu/updateSimpleMenu',
    async (data, { rejectWithValue }) => {
        try {
            const { menu_id: menuId } = data;
            const response = await MenuService.updateSimpleMenu(data, menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getParentMenuList = createAsyncThunk(
    'menu/getParentMenuList',
    async (menuId, { rejectWithValue }) => {
        try {
            const response = await MenuService.getParentMenuList(menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getSortMenuList = createAsyncThunk(
    'menu/getSortMenuList',
    async (menuId, { rejectWithValue }) => {
        try {
            const response = await MenuService.getSortMenuList(menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getAdvancedMenuList = createAsyncThunk(
    'menu/getAdvancedMenuList',
    async (menuId, { rejectWithValue }) => {
        try {
            const response = await MenuService.getAdvancedMenuList(menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const updateMenuLabel = createAsyncThunk(
    'menu/updateMenuLabel',
    async (data, { rejectWithValue }) => {
        try {
            const { id: menuId } = data;
            const response = await MenuService.updateMenuLabel(data, menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const viewAdvancedMenu = createAsyncThunk(
    'menu/viewAdvancedMenu',
    async (menuId, { rejectWithValue }) => {
        try {
            const response = await MenuService.viewAdvancedMenu(menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const updateAdvancedMenu = createAsyncThunk(
    'menu/updateAdvancedMenu',
    async (data, { rejectWithValue }) => {
        try {
            const { menu_id: menuId } = data;
            const response = await MenuService.updateAdvancedMenu(data, menuId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const updateMenuSlice = createSlice({
    name: 'updateMenu',
    initialState: model,
    reducers: {
        resetMenuStateData: (state) => {
            state.data = null;
            state.advancedMenuList = [];
        },
        resetMenuStateOnError: (state) => {
            state.errors = null;
        },
        resetMenuStateOnSuccess: (state) => {
            state.successOnMenuUpdate = '';
        },
        resetLabelStateOnSuccess: (state) => {
            state.successOnLabelUpdate = '';
        },
    },
    extraReducers: (builder) => {
        // view menu
        builder.addCase(viewMenu.pending, (state) => {
            state.loading = true;
        }).addCase(viewMenu.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewMenu.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // update simple menu
        builder.addCase(updateSimpleMenu.pending, (state) => {
            state.submitting = true;
        }).addCase(updateSimpleMenu.fulfilled, (state) => {
            state.submitting = false;
            state.successOnMenuUpdate = 'Menu updated successfully!';
        }).addCase(updateSimpleMenu.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // get parent menu list
        builder.addCase(getParentMenuList.pending, (state) => {
            state.loading = true;
        }).addCase(getParentMenuList.fulfilled, (state, action) => {
            state.loading = false;
            state.parentMenuList = action.payload;
        }).addCase(getParentMenuList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get sort menu list
        builder.addCase(getSortMenuList.pending, (state) => {
            state.loading = true;
        }).addCase(getSortMenuList.fulfilled, (state, action) => {
            state.loading = false;
            state.sortMenuList = action.payload;
        }).addCase(getSortMenuList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get advanced menu list
        builder.addCase(getAdvancedMenuList.pending, (state) => {
            state.loading = true;
        }).addCase(getAdvancedMenuList.fulfilled, (state, action) => {
            state.loading = false;
            state.advancedMenuList = action.payload;
        }).addCase(getAdvancedMenuList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // update label menu
        builder.addCase(updateMenuLabel.pending, (state) => {
            state.submitting = true;
        }).addCase(updateMenuLabel.fulfilled, (state) => {
            state.submitting = false;
            state.successOnLabelUpdate = 'Menu Label updated successfully!';
        }).addCase(updateMenuLabel.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // view advanced menu
        builder.addCase(viewAdvancedMenu.pending, (state) => {
            state.loading = true;
        }).addCase(viewAdvancedMenu.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewAdvancedMenu.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // update advanced menu
        builder.addCase(updateAdvancedMenu.pending, (state) => {
            state.submitting = true;
        }).addCase(updateAdvancedMenu.fulfilled, (state) => {
            state.submitting = false;
            state.successOnMenuUpdate = 'Advanced Menu updated successfully!';
        }).addCase(updateAdvancedMenu.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetMenuStateData,
    resetMenuStateOnError,
    resetMenuStateOnSuccess,
    resetLabelStateOnSuccess,
} = updateMenuSlice.actions;

export default updateMenuSlice.reducer;
